import React, { useEffect, useState } from "react";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import { ToastContainer } from "react-toastify";

import HeaderOrg from "../Headers/HeaderOrg";
import ManageTitle from "../../Components/TitleElements/ManageTitle";
import Loader from "../../Components/Loader/Loader";
import Help from "../../Components/Help/Help";
import Button from "../../Components/Button/Button";
import { useCan } from "../../device/DeviceContext";
import { ShowToastMessage } from "../../helper/ShowToastMessage";
import { serviceConsumer } from "../../network/ServiceConsumer";
import {
  getTabulationStatus,
  downloadEventDayPdf,
  downloadEventPdf,
} from "../../network/oireachtasEventDay";

// Print — one picker for the event-day paper artifacts (Devin's IA,
// 2026-08-17: Print section tiles all land here, preselected via the URL).
// The generators already exist; this screen only selects and downloads:
//   number cards / check-in sheet (per round) / the judges' score sheets (all
//   rounds or one; a recall round once the recall is published) need a
//   LOCKED draw (a recall round's check-in, like its score sheets, lists the
//   recalled dancers and prints once the recall is published); the round 3
//   musician sheet needs the published recall; a per-comp result PDF needs that comp PUBLISHED; the full
//   results book covers every published comp.
// fn: the job function an event-day device needs to print it (card 540f21a4)
const ARTIFACTS = [
  { key: "results", label: "Results", needs: "published", fn: "ox.print.results" },
  { key: "number-cards", label: "Competitor #'s (number cards)", needs: "locked", fn: "ox.print.cards" },
  { key: "check-in", label: "Check-in sheets", needs: "locked", fn: "ox.print.sheets" },
  { key: "score-sheets", label: "Score sheets", needs: "locked", fn: "ox.print.sheets" },
  // round 3: the recalled dancers' numbers with the set to play (Devin 2026-11-03)
  { key: "musician", label: "Round 3 – Musician", needs: "recall", fn: "ox.print.sheets" },
];
// what a competition needs before an artifact prints
const ready = (needs, r) =>
  needs === "published"
    ? r.resultsPublished
    : needs === "recall"
      ? r.draw.locked && r.competition.rounds >= 3 && r.recallPublished
      : r.draw.locked;
const notReady = (needs, r) =>
  needs === "published"
    ? "not published"
    : needs === "recall"
      ? r.competition.rounds < 3
        ? "no recall round"
        : "recall not published"
      : "draw not locked";

const OireachtasPrint = () => {
  const { artifact: artifactParam } = useParams();
  const can = useCan();
  const artifacts = ARTIFACTS.filter((a) => can(a.fn));
  const [artifact, setArtifact] = useState(
    artifacts.some((a) => a.key === artifactParam) ? artifactParam : (artifacts[0] || ARTIFACTS[0]).key
  );
  const [eventDoc, setEventDoc] = useState(null);
  const [rows, setRows] = useState([]); // tabulation-status rows (draw + publish state)
  const [selected, setSelected] = useState("");
  const [round, setRound] = useState(1);
  // score sheets: 0 = every round whose dancers are known; a recall round
  // prints once the recall is published, with the recalled numbers only
  const [sheetRound, setSheetRound] = useState(0);
  const [loading, setLoading] = useState(false);
  const [busy, setBusy] = useState(false);
  const navigate = useNavigate();
  const location = useLocation();
  const eventId = new URLSearchParams(location.search).get("id");

  useEffect(() => {
    (async () => {
      try {
        setLoading(true);
        const [eventRes, status] = await Promise.all([
          serviceConsumer("GET", `${process.env.REACT_APP_BASE_URL}/oireachtas/${eventId}`),
          getTabulationStatus(eventId),
        ]);
        setEventDoc(eventRes.event);
        setRows(status.rows || []);
      } catch (e) {
        ShowToastMessage("error", e?.messages || "Could not load competitions");
      } finally {
        setLoading(false);
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [eventId]);

  // the job is re-read every minute: a print it no longer includes is
  // replaced by one it does (MC review c87cf2d7 R1-F5)
  const allowedKeys = artifacts.map((a) => a.key).join(",");
  useEffect(() => {
    if (artifacts.length && !artifacts.some((a) => a.key === artifact)) switchArtifact(artifacts[0].key);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [allowedKeys]);
  const allowed = artifacts.some((a) => a.key === artifact);

  const needs = ARTIFACTS.find((a) => a.key === artifact).needs;
  const eligible = (r) => ready(needs, r);
  const selectedRow = rows.find((r) => String(r.competition._id) === String(selected));

  const switchArtifact = (key) => {
    setArtifact(key);
    setRound(1);
    setSheetRound(0);
    // Deselect if the current comp isn't eligible for the new artifact.
    const row = rows.find((r) => String(r.competition._id) === String(selected));
    const stillOk = row && ready(ARTIFACTS.find((a) => a.key === key).needs, row);
    if (!stillOk) setSelected("");
  };

  const download = async () => {
    const c = selectedRow.competition;
    try {
      setBusy(true);
      if (artifact === "number-cards")
        await downloadEventDayPdf(eventId, c._id, "cards.pdf", `${c.code}-cards.pdf`);
      else if (artifact === "check-in") {
        // a recall round waits for the published recall (its sheet lists the recalled dancers)
        const r = round >= 3 && c.rounds >= 3 && !selectedRow.recallPublished ? 1 : round;
        await downloadEventDayPdf(eventId, c._id, `checkin.pdf?round=${r}`, `${c.code}-checkin-r${r}.pdf`);
      } else if (artifact === "musician")
        await downloadEventDayPdf(eventId, c._id, "musician.pdf?round=3", `${c.code}-musician-r3.pdf`);
      else if (artifact === "score-sheets") {
        // a round this competition can't print yet (or doesn't have) is "all rounds"
        const r =
          sheetRound <= c.rounds && (sheetRound < 3 || selectedRow.recallPublished) ? sheetRound : 0;
        await downloadEventDayPdf(
          eventId,
          c._id,
          r ? `scoresheets.pdf?round=${r}` : "scoresheets.pdf",
          `${c.code}-scoresheets${r ? `-r${r}` : ""}.pdf`
        );
      }
      else
        await downloadEventPdf(eventId, `results/${c._id}/results.pdf`, `${c.code}-results.pdf`);
    } catch (e) {
      ShowToastMessage("error", e?.messages || "The PDF could not be opened");
    } finally {
      setBusy(false);
    }
  };

  const downloadBook = async () => {
    try {
      setBusy(true);
      await downloadEventPdf(eventId, "results-book.pdf", "results-book.pdf");
    } catch (e) {
      ShowToastMessage("error", e?.messages || "The PDF could not be opened (is anything published yet?)");
    } finally {
      setBusy(false);
    }
  };

  const eventDate = (eventDoc?.eventDays && eventDoc.eventDays[0]) || eventDoc?.entriesCloseDate;

  return (
    <>
      {loading && <Loader />}
      <HeaderOrg />
      <ManageTitle
        eventLogo={eventDoc?.logo}
        eventName={eventDoc?.name}
        eventType={eventDoc?.eventType}
        eventDate={eventDate}
        loading={loading}
        onBackClick={() => navigate(`/oireachtas/manage?id=${eventId}`)}
      />
      {!loading && (
        <div className="db-body pb-5">
          <div className="grey-42 mt-3" style={{ fontSize: "13px" }}>
            Every paper artifact regenerates from the locked draw, so reprints always match the
            book. Number cards, check-in and score sheets need a locked draw (round 3 check-in, score
            sheets and the musician sheet once the recall is published); result PDFs need the
            competition published.
          </div>

          <div className="d-md-flex mt-4 gap-md-4 align-items-end flex-wrap">
            <div>
              <div className="search-label">Print:</div>
              <select
                className="check-in-select"
                value={artifact}
                onChange={(e) => switchArtifact(e.target.value)}
              >
                {artifacts.map((a) => (
                  <option key={a.key} value={a.key}>{a.label}</option>
                ))}
              </select>
            </div>
            <div>
              <div className="search-label">Competition:</div>
              <select
                className="check-in-select"
                value={selected}
                onChange={(e) => {
                  // rounds belong to the competition: start the new one on its defaults
                  setSelected(e.target.value);
                  setRound(1);
                  setSheetRound(0);
                }}
              >
                <option value="">Select…</option>
                {rows.map((r) => (
                  <option
                    key={r.competition._id}
                    value={r.competition._id}
                    disabled={!eligible(r)}
                  >
                    {r.competition.code} —{" "}
                    {eligible(r) ? `${r.draw.count} in draw` : notReady(needs, r)}
                  </option>
                ))}
              </select>
            </div>
            {artifact === "check-in" && selectedRow && (
              <div>
                <div className="search-label">Round:</div>
                <select
                  className="check-in-select"
                  value={round}
                  onChange={(e) => setRound(Number(e.target.value))}
                >
                  {Array.from({ length: selectedRow.competition.rounds }, (_, i) => i + 1).map((r) => {
                    const recall = r >= 3 && selectedRow.competition.rounds >= 3;
                    return (
                      <option key={r} value={r} disabled={recall && !selectedRow.recallPublished}>
                        Round {r}
                        {recall ? (selectedRow.recallPublished ? " (recalled dancers)" : " — after the recall") : ""}
                      </option>
                    );
                  })}
                </select>
              </div>
            )}
            {artifact === "score-sheets" && selectedRow && selectedRow.competition.rounds > 1 && (
              <div>
                <div className="search-label">Round:</div>
                <select
                  className="check-in-select"
                  value={sheetRound}
                  onChange={(e) => setSheetRound(Number(e.target.value))}
                >
                  <option value={0}>
                    {selectedRow.recallPublished || selectedRow.competition.rounds < 3
                      ? "All rounds"
                      : "Rounds 1–2 (round 3 after the recall)"}
                  </option>
                  {Array.from({ length: selectedRow.competition.rounds }, (_, i) => i + 1).map((r) => (
                    <option key={r} value={r} disabled={r >= 3 && !selectedRow.recallPublished}>
                      Round {r}
                      {r >= 3 ? (selectedRow.recallPublished ? " (recalled dancers)" : " — after the recall") : ""}
                    </option>
                  ))}
                </select>
              </div>
            )}
            <div className="d-flex gap-2">
              <Button
                className={selected && !busy && allowed ? "filled" : "disabled"}
                disabled={!selected || busy || !allowed}
                onClick={download}
              >
                {busy ? "Preparing…" : "Open PDF"}
              </Button>
              {artifact === "results" && allowed && (
                <Button className="outlined" disabled={busy} onClick={downloadBook}>
                  Full results book
                </Button>
              )}
            </div>
          </div>

          {!selected && (
            <div className="d-flex justify-content-center my-5 grey-42">
              Select a competition
            </div>
          )}
        </div>
      )}
      <Help />
      <ToastContainer />
    </>
  );
};

export default OireachtasPrint;
